'use strict';

const { clearCachedCart } = require('public/js/lib/cart-cache');
const csrf = require('public/js/lib/csrf/utils');

const ADD_ITEM_ENDPOINT = '/ajax/cart/update';
const EMPTY_CART_ENDPOINT = '/ajax/cart/empty';

/**
 * Mirrors of `Common\Infra\ExceptionCodes` surfaced by `CartController`
 * on `errors[].code`. Mirrored (not imported) because the source
 * package is PHP-only.
 *
 * @see vendor/revolutionparts/common/tools/rp_common/Infra/ExceptionCodes.php
 */
const ExceptionCodes = {
	CART_FULL: 15108,
	CART_MISSING_REQUIRED_SUPPLIER_FOR_LEADGEN: 15109,
	CART_SINGLE_WAREHOUSE_RESTRICTION: 15104,
};

/**
 * Outcome of an add-to-cart write, produced by `classifyAddToCartResult`.
 * Domain-owned: names happen to align with the shopping-agent SDK's
 * `errorCode` values, but other callers can remap freely. `FAILURE`
 * is the generic catch-all (HTTP 5xx, unrecognized codes, etc.) —
 * carries a message but no actionable kind beyond "it failed".
 */
const AddToCartOutcomeKind = Object.freeze({
	CART_FULL: 'CART_FULL',
	FAILURE: 'FAILURE',
	NO_SUPPLIER: 'NO_SUPPLIER',
	PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
	SUCCESS: 'SUCCESS',
	WAREHOUSE_RESTRICTION: 'WAREHOUSE_RESTRICTION',
});

/**
 * @typedef {(
 *   | { kind: 'SUCCESS', cart: object|null }
 *   | { kind: 'CART_FULL'|'NO_SUPPLIER'|'WAREHOUSE_RESTRICTION'|'PRODUCT_NOT_FOUND'|'FAILURE', message: string }
 * )} AddToCartOutcome
 */

/**
 * Envelope returned by `addProductToCart` / `emptyCart`. `error` is
 * client-side/network detail; server-side conditions live on `data`
 * and `status`.
 *
 * @typedef {{ ok: boolean, status: number, data: object|null, error: string|null }} CartWriteResult
 */

/**
 * JSON POST with CSRF returning a flat `CartWriteResult`. Pass
 * `undefined` for no body.
 *
 * @param {string} url
 * @param {object|undefined} body
 * @returns {Promise<CartWriteResult>}
 */
function postJsonWithCsrf(url, body) {
	if (typeof fetch !== 'function') {
		return Promise.resolve({ ok: false, status: 0, data: null, error: 'fetch is not available' });
	}

	const init = {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Accept': 'application/json',
			'Content-Type': 'application/json',
		},
	};
	if (body !== undefined) {
		init.body = JSON.stringify(body);
	}

	return csrf.fetchWithCsrf(url, init).then(function (response) {
		return response.json().then(
			function (data) { return { ok: response.ok, status: response.status, data: data, error: null }; },
			function () { return { ok: response.ok, status: response.status, data: null, error: null }; },
		);
	}, function (err) {
		return { ok: false, status: 0, data: null, error: err?.message || 'request failed' };
	});
}

/**
 * Refreshes the on-page header cart after a successful write. Mirrors
 * the in-tree pattern from `public/js/cart.js` updateCart and
 * `components/cart.js` save_product: clear the cache, then let
 * `rp_app.cart.load_cart` re-hydrate from `/ajax/cart`. Best-effort —
 * `rp_app.cart` only exists on pages that import `components/cart.js`.
 */
function refreshHeaderCart() {
	try {
		clearCachedCart();
	} catch (_e) {
		// Storage unavailable — server is source of truth; stale cache
		// recovers on next page load.
	}
	try {
		globalThis.rp_app?.cart?.load_cart?.();
	} catch (_e) {
		// Best-effort; see above.
	}
}

/**
 * Coerces a positive-integer quantity. `undefined` → 1. Anything
 * non-finite, non-positive, or non-integer collapses to 0 — the
 * caller's "invalid" sentinel.
 *
 * @param {unknown} value
 * @returns {number}
 */
function coerceQuantity(value) {
	if (value === undefined) {
		return 1;
	}
	if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
		return 0;
	}
	return value;
}

/**
 * Adds a product via the cart-update endpoint. Sends the minimal
 * payload (`sku_stripped`, `catalog`, `quantity`); the controller's
 * `loadFullProduct` fills the rest. Optional `cartSource` is forwarded
 * as `cart_source` for caller attribution. Refreshes the header cart
 * on success. Does NOT enforce variant-free products — caller's job.
 *
 * @param {{ sku: string, catalog: string, quantity?: number, cartSource?: string }} product
 * @returns {Promise<CartWriteResult>}
 */
function addProductToCart(product) {
	const sku = typeof product?.sku === 'string' ? product.sku : '';
	const catalog = typeof product?.catalog === 'string' ? product.catalog : '';
	const quantity = coerceQuantity(product?.quantity);

	if (sku === '' || catalog === '' || quantity === 0) {
		return Promise.resolve({ ok: false, status: 0, data: null, error: 'invalid arguments' });
	}

	const body = {
		sku_stripped: sku,
		catalog: catalog,
		quantity: quantity,
	};
	const cartSource = product?.cartSource;
	if (typeof cartSource === 'string' && cartSource !== '') {
		body.cart_source = cartSource;
	}

	return postJsonWithCsrf(ADD_ITEM_ENDPOINT, body).then(function (result) {
		// HTTP 200 doesn't imply mutation — the controller can signal
		// failure via `status: 'fail'` or a non-empty `errors[]`. Skip
		// the refresh in those cases.
		const mutated = result.ok
			&& result.data?.status !== 'fail'
			&& !(Array.isArray(result.data?.errors) && result.data.errors.length > 0);
		if (mutated) {
			refreshHeaderCart();
		}
		return result;
	});
}

/**
 * Empties the cart via the cart-empty endpoint. The controller returns
 * 200 with `{ status: 'fail' }` when there's no cart to clear (an
 * idempotent no-op) — callers decide how to surface that. Refreshes
 * the header cart on HTTP success regardless of body `status` since
 * the cache is stale either way.
 *
 * @returns {Promise<CartWriteResult>}
 */
function emptyCart() {
	return postJsonWithCsrf(EMPTY_CART_ENDPOINT, undefined).then(function (result) {
		if (result.ok) {
			refreshHeaderCart();
		}
		return result;
	});
}

/**
 * Cart-add failure codes in priority order. A single response can
 * carry multiple `errors[]`; the first match in this table wins so
 * the outcome is deterministic.
 */
const HLD_PRIORITY = [
	{ code: ExceptionCodes.CART_FULL, kind: AddToCartOutcomeKind.CART_FULL, fallbackMessage: 'cart is full' },
	{ code: ExceptionCodes.CART_MISSING_REQUIRED_SUPPLIER_FOR_LEADGEN, kind: AddToCartOutcomeKind.NO_SUPPLIER, fallbackMessage: 'no supplier selected' },
	{ code: ExceptionCodes.CART_SINGLE_WAREHOUSE_RESTRICTION, kind: AddToCartOutcomeKind.WAREHOUSE_RESTRICTION, fallbackMessage: 'warehouse restriction' },
];

/**
 * First `errors[]` entry whose `code` matches, or `null`.
 *
 * @param {unknown} errors
 * @param {number} code
 * @returns {{ code?: number, message?: string }|null}
 */
function findErrorByCode(errors, code) {
	if (!Array.isArray(errors)) {
		return null;
	}
	for (const entry of errors) {
		if (entry && typeof entry === 'object' && entry.code === code) {
			return entry;
		}
	}
	return null;
}

/**
 * `errors[0].message` if it's a string, otherwise `fallback`.
 *
 * @param {unknown} errors
 * @param {string} fallback
 * @returns {string}
 */
function firstErrorMessage(errors, fallback) {
	const firstError = Array.isArray(errors) && errors.length > 0 ? errors[0] : null;
	return typeof firstError?.message === 'string' ? firstError.message : fallback;
}

/**
 * Whether `data` looks like a `Cart::toArray()` body — distinguishes
 * cart-shaped success/warning responses from `{ status: 'fail' }` and
 * unrelated 2xx bodies.
 *
 * @param {unknown} data
 * @returns {boolean}
 */
function isCartShaped(data) {
	return data?.id !== undefined && Array.isArray(data?.items);
}

/**
 * Human-readable error message from a failed `CartWriteResult`.
 * Prefers `data.message` (server), falls back to `error` (client/
 * network), then to a status-code string.
 *
 * @param {CartWriteResult} result
 * @returns {string}
 */
function errorMessageFromCartWriteResult(result) {
	if (typeof result.data?.message === 'string') {
		return result.data.message;
	}
	if (typeof result.error === 'string' && result.error !== '') {
		return result.error;
	}
	return 'request failed: ' + result.status;
}

/**
 * Classifies a `CartWriteResult` from `addProductToCart` into a
 * cart-domain outcome:
 *
 *   - `CART_FULL` / `NO_SUPPLIER` / `WAREHOUSE_RESTRICTION`: one of
 *     the three exception codes in `errors[]`, picked by `HLD_PRIORITY`.
 *   - `PRODUCT_NOT_FOUND`: HTTP 422 (controller hard-codes `code: 0`,
 *     so we key off status).
 *   - `FAILURE`: any other non-2xx, plus 2xx bodies whose
 *     `status: 'fail'` or non-empty `errors[]` indicate the write
 *     didn't land.
 *   - `SUCCESS`: clean cart body, or any 2xx with no failure indicator.
 *
 * @param {CartWriteResult} result
 * @returns {AddToCartOutcome}
 */
function classifyAddToCartResult(result) {
	const errors = result.data?.errors;

	for (const { code, kind, fallbackMessage } of HLD_PRIORITY) {
		const found = findErrorByCode(errors, code);
		if (found) {
			return { kind, message: found.message || fallbackMessage };
		}
	}

	let kind = AddToCartOutcomeKind.SUCCESS;
	let message = '';
	if (result.status === 422) {
		kind = AddToCartOutcomeKind.PRODUCT_NOT_FOUND;
		message = firstErrorMessage(errors, 'product not found');
	} else if (!result.ok) {
		kind = AddToCartOutcomeKind.FAILURE;
		message = errorMessageFromCartWriteResult(result);
	} else if (result.data?.status === 'fail' || (isCartShaped(result.data) && Array.isArray(errors) && errors.length > 0)) {
		kind = AddToCartOutcomeKind.FAILURE;
		message = firstErrorMessage(errors, 'cart update failed');
	}

	return kind === AddToCartOutcomeKind.SUCCESS
		? { kind, cart: result.data }
		: { kind, message };
}

module.exports = {
	AddToCartOutcomeKind,
	ExceptionCodes,
	addProductToCart,
	classifyAddToCartResult,
	emptyCart,
};
